import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Tabs,
  Card,
  Empty,
  List,
  Spin,
  Tag,
  Badge,
  Button,
  message,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  getMyNotifications,
  readMyNotification,
} from "../repository/data.repository";
import { MyNotification } from "../repository/data.default";
import { getProblemMessage } from "../utils/http";

// PD-02 §7.1 Company Notification Inbox — company-facing alerts, reminders,
// operational messages, system notices, policy/assessment/training/event
// alerts and action updates. Sending messages / template management lives
// upstream in Admin UI; here we show the inbox and let the user mark read.
function NotificationsPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<MyNotification[]>([]);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  function load() {
    setLoading(true);
    getMyNotifications({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: 100,
      sortBy: "sentAt",
      sortDescending: true,
    })
      .then((data) => setItems(data.data ?? []))
      .catch((err) => {
        message.error(
          getProblemMessage(err) || "Failed to load notifications.",
        );
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function markRead(uid: string) {
    try {
      await readMyNotification({ uid });
      setItems((prev) =>
        prev.map((n) =>
          n.uid === uid
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n,
        ),
      );
    } catch (err) {
      message.error(getProblemMessage(err) || "Could not mark as read.");
    }
  }

  const visible = items.filter((n) => (filter === "unread" ? !n.isRead : true));
  const unreadCount = items.filter((n) => !n.isRead).length;

  return (
    <Col className="flex flex-auto flex-col gap-4 overflow-y-auto p-4">
      <Row className="items-center justify-between">
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faBell} className="mr-2" />
          Notifications
          {unreadCount > 0 && (
            <Badge count={unreadCount} className="ml-3" overflowCount={99} />
          )}
        </Typography.Title>
      </Row>

      <Tabs
        activeKey={filter}
        onChange={(k) => setFilter(k as "all" | "unread")}
        items={[
          { key: "all", label: `All (${items.length})` },
          { key: "unread", label: `Unread (${unreadCount})` },
        ]}
      />

      {loading ? (
        <Card bodyStyle={{ padding: 32, textAlign: "center" }}>
          <Spin />
        </Card>
      ) : !visible.length ? (
        <Card bodyStyle={{ padding: 16 }}>
          <Empty
            description={
              <Typography.Text type="secondary">
                {filter === "unread"
                  ? "No unread notifications."
                  : "You're all caught up."}
              </Typography.Text>
            }
          />
        </Card>
      ) : (
        <Card bodyStyle={{ padding: 0 }}>
          <List
            dataSource={visible}
            renderItem={(n) => (
              <List.Item
                actions={
                  n.isRead
                    ? []
                    : [
                        <Button
                          size="small"
                          icon={<FontAwesomeIcon icon={faCheck} />}
                          onClick={() => markRead(n.uid)}
                        >
                          Mark read
                        </Button>,
                      ]
                }
                style={{
                  background: n.isRead ? undefined : "#f6ffed",
                  paddingLeft: 16,
                  paddingRight: 16,
                }}
              >
                <List.Item.Meta
                  title={
                    <Row className="items-center justify-between">
                      <span>{n.title}</span>
                      {!n.isRead && <Tag color="blue">New</Tag>}
                    </Row>
                  }
                  description={
                    <>
                      <Typography.Paragraph className="!mb-1">
                        {n.message}
                      </Typography.Paragraph>
                      <Typography.Text type="secondary" className="text-xs">
                        {new Date(n.sentAt).toLocaleString("en-GB")}
                        {n.sentByUserName && ` · from ${n.sentByUserName}`}
                      </Typography.Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </Col>
  );
}

export default NotificationsPage;
